// frontend-react/src/components/ModelPricingModal.tsx
// Gemini 模型定價管理 Modal（Super Admin 專用）
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import apiClient from '../services/apiClient';
import { getApiBaseUrl } from '../config/api';

const { Text } = Typography;

interface PricingTier {
  id?: number;
  tier_order: number;
  threshold_tokens: number;
  price_per_million: number;
}

interface ModelRates {
  [tokenType: string]: PricingTier[];
}

interface ModelPricingEntry {
  model_pattern: string;
  rates: ModelRates;
}

const TOKEN_TYPES = ['input', 'output', 'thinking', 'cached'] as const;
type TokenType = (typeof TOKEN_TYPES)[number];

const TOKEN_TYPE_LABELS: Record<TokenType, string> = {
  input: 'Input（提示詞）',
  output: 'Output（輸出）',
  thinking: 'Thinking（思考）',
  cached: 'Cached（快取命中）',
};

const TOKEN_TYPE_COLORS: Record<TokenType, string> = {
  input: 'blue',
  output: 'green',
  thinking: 'purple',
  cached: 'orange',
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const ModelPricingModal: React.FC<Props> = ({ open, onClose }) => {
  const apiBase = getApiBaseUrl();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState<ModelPricingEntry[]>([]);
  const [newModelName, setNewModelName] = useState('');

  const fetchPricing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`${apiBase}/api/v1/platform/model-pricing`);
      setPricing(res.data.pricing || []);
    } catch {
      message.error('載入定價資料失敗');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (open) fetchPricing();
  }, [open, fetchPricing]);

  const handleSaveModel = async (entry: ModelPricingEntry) => {
    setSaving(true);
    try {
      await apiClient.post(`${apiBase}/api/v1/platform/model-pricing`, {
        model_pattern: entry.model_pattern,
        rates: entry.rates,
      });
      message.success(`${entry.model_pattern} 定價已儲存`);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModel = async (model_pattern: string) => {
    try {
      await apiClient.delete(
        `${apiBase}/api/v1/platform/model-pricing/${encodeURIComponent(model_pattern)}`
      );
      message.success(`${model_pattern} 定價已刪除`);
      fetchPricing();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '刪除失敗');
    }
  };

  const handleAddModel = () => {
    const name = newModelName.trim();
    if (!name) return;
    if (pricing.some((p) => p.model_pattern === name)) {
      message.warning('此模型已存在');
      return;
    }
    const emptyRates: ModelRates = {};
    TOKEN_TYPES.forEach((tt) => {
      emptyRates[tt] = [{ tier_order: 0, threshold_tokens: 0, price_per_million: 0 }];
    });
    setPricing((prev) => [...prev, { model_pattern: name, rates: emptyRates }]);
    setNewModelName('');
  };

  const handleTierChange = (
    modelIdx: number,
    tokenType: string,
    tierIdx: number,
    field: 'threshold_tokens' | 'price_per_million',
    value: number | null
  ) => {
    setPricing((prev) => {
      const next = [...prev];
      const src = next[modelIdx];
      if (!src) return prev;
      const entry: ModelPricingEntry = { model_pattern: src.model_pattern, rates: { ...src.rates } };
      const tiers = [...(entry.rates[tokenType] || [])];
      const current = tiers[tierIdx]!;
      tiers[tierIdx] = { ...current, [field]: value ?? 0 };
      entry.rates[tokenType] = tiers;
      next[modelIdx] = entry;
      return next;
    });
  };

  const handleAddTier = (modelIdx: number, tokenType: string) => {
    setPricing((prev) => {
      const next = [...prev];
      const src = next[modelIdx];
      if (!src) return prev;
      const entry: ModelPricingEntry = { model_pattern: src.model_pattern, rates: { ...src.rates } };
      const tiers = [...(entry.rates[tokenType] || [])];
      tiers.push({
        tier_order: tiers.length,
        threshold_tokens: 0,
        price_per_million: 0,
      });
      entry.rates[tokenType] = tiers;
      next[modelIdx] = entry;
      return next;
    });
  };

  const handleRemoveTier = (modelIdx: number, tokenType: string, tierIdx: number) => {
    setPricing((prev) => {
      const next = [...prev];
      const src = next[modelIdx];
      if (!src) return prev;
      const entry: ModelPricingEntry = { model_pattern: src.model_pattern, rates: { ...src.rates } };
      const tiers = [...(entry.rates[tokenType] || [])];
      tiers.splice(tierIdx, 1);
      entry.rates[tokenType] = tiers.map((t, i) => ({ ...t, tier_order: i }));
      next[modelIdx] = entry;
      return next;
    });
  };

  return (
    <Modal
      title="Gemini 模型定價管理"
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      destroyOnClose
    >
      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        <input
          placeholder="輸入新模型名稱，例如：gemini-2.5-pro"
          value={newModelName}
          onChange={(e) => setNewModelName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddModel()}
          style={{
            flex: 1,
            padding: '4px 8px',
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            fontSize: 13,
          }}
        />
        <Button icon={<PlusOutlined />} onClick={handleAddModel} type="default">
          新增模型
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>載入中...</div>
      ) : pricing.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>尚無定價資料</div>
      ) : (
        pricing.map((entry, modelIdx) => (
          <div
            key={entry.model_pattern}
            style={{
              border: '1px solid #e8e5df',
              borderRadius: 8,
              marginBottom: 16,
              padding: '12px 16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <Text strong style={{ fontSize: 14 }}>
                {entry.model_pattern}
              </Text>
              <Space>
                <Button
                  type="primary"
                  size="small"
                  loading={saving}
                  onClick={() => handleSaveModel(entry)}
                >
                  儲存
                </Button>
                <Popconfirm
                  title={`確定刪除 ${entry.model_pattern} 的所有定價？`}
                  onConfirm={() => handleDeleteModel(entry.model_pattern)}
                  okText="刪除"
                  cancelText="取消"
                >
                  <Button size="small" danger icon={<DeleteOutlined />}>
                    刪除模型
                  </Button>
                </Popconfirm>
              </Space>
            </div>

            {TOKEN_TYPES.map((tokenType) => {
              const tiers = entry.rates[tokenType] || [];
              return (
                <div key={tokenType} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Tag color={TOKEN_TYPE_COLORS[tokenType]} style={{ margin: 0 }}>
                      {TOKEN_TYPE_LABELS[tokenType]}
                    </Tag>
                    {tokenType === 'thinking' && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        （預設 $0，目前費用含在 Output 中）
                      </Text>
                    )}
                  </div>

                  {tiers.map((tier, tierIdx) => (
                    <div
                      key={tierIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 4,
                        paddingLeft: 12,
                      }}
                    >
                      <Text style={{ fontSize: 12, color: '#666', minWidth: 42 }}>
                        階梯 {tierIdx + 1}
                      </Text>
                      <Text style={{ fontSize: 12, color: '#999' }}>超過</Text>
                      <InputNumber
                        size="small"
                        min={0}
                        step={10000}
                        value={tier.threshold_tokens}
                        onChange={(v) =>
                          handleTierChange(modelIdx, tokenType, tierIdx, 'threshold_tokens', v)
                        }
                        style={{ width: 110 }}
                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(v) => Number((v || '').replace(/,/g, ''))}
                      />
                      <Text style={{ fontSize: 12, color: '#999' }}>tokens 後，$</Text>
                      <InputNumber
                        size="small"
                        min={0}
                        step={0.001}
                        precision={6}
                        value={tier.price_per_million}
                        onChange={(v) =>
                          handleTierChange(modelIdx, tokenType, tierIdx, 'price_per_million', v)
                        }
                        style={{ width: 100 }}
                      />
                      <Text style={{ fontSize: 12, color: '#999' }}>/1M tokens</Text>
                      {tiers.length > 1 && (
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveTier(modelIdx, tokenType, tierIdx)}
                        />
                      )}
                    </div>
                  ))}

                  <Button
                    size="small"
                    type="dashed"
                    icon={<PlusOutlined />}
                    style={{ marginLeft: 12, marginTop: 2 }}
                    onClick={() => handleAddTier(modelIdx, tokenType)}
                  >
                    新增階梯
                  </Button>
                </div>
              );
            })}
          </div>
        ))
      )}
    </Modal>
  );
};

export default ModelPricingModal;
